import { useEffect, useRef, useState } from "react";
import ReactPlayer from "react-player";
import { VideoPlayer } from "components/Interactive/VideoPlayer";
import { ExerciseHeader } from "components/Common/ExerciseHeader";
import { Space } from "components/Common/Space";
import { Paragraph } from "components/Typography/Paragraph";
import { Button } from "components/Interactive/Button";
import { LessonDetailsContent } from "content";
import {
  TDescribeVideoExercise,
  TDescribeVideoExercisePayload,
} from "types/app/exercises";
import { getYouTubeThumbnail, sanitizeYouTubeUrl } from "utils/common";
import "./DescribeVideoExercise.styles.scss";

// Browsers often silently reject unmuted autoplay outside a user gesture;
// if playback hasn't started by this point, assume it was blocked.
const UNMUTED_AUTOPLAY_FALLBACK_DELAY_MS = 1500;

type TDescribeVideoExerciseProps = {
  /**
   * Chapter ID to which the exercise belongs.
   */
  chapterId: number;
  /**
   * An exercise raw data from API response.
   */
  exercise: TDescribeVideoExercise;
  /**
   * Lesson chapter order number with the exercise sequence number to display before the exercise condition.
   * @default null
   */
  prefix?: string | null;
  /**
   * When set, this exercise is the deep-link target: the player mounts eagerly,
   * seeks to this many seconds, and attempts autoplay.
   * @default undefined
   */
  autoPlayStartSeconds?: number;
};

export const DescribeVideoExercise: React.FC<TDescribeVideoExerciseProps> = (
  props
) => {
  const { chapterId, exercise, prefix = null, autoPlayStartSeconds } = props;

  const videoUrl = sanitizeYouTubeUrl(exercise.payload.data.videoUrl);
  const isDeepLinked = autoPlayStartSeconds !== undefined;
  const playerRef = useRef<ReactPlayer | null>(null);
  const isPlayingRef = useRef(false);
  const [playing, setPlaying] = useState(isDeepLinked);
  const [muted, setMuted] = useState(false);
  const [showUnmuteAffordance, setShowUnmuteAffordance] = useState(false);
  const unmuteFallbackTimer = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    if (!isDeepLinked) return;

    unmuteFallbackTimer.current = setTimeout(() => {
      // Playback never started at all within the window — assume the
      // browser blocked unmuted autoplay outright and fall back to muted.
      if (!isPlayingRef.current) {
        setMuted(true);
      }
    }, UNMUTED_AUTOPLAY_FALLBACK_DELAY_MS);

    return () => clearTimeout(unmuteFallbackTimer.current);
  }, [isDeepLinked]);

  useEffect(() => {
    // Only needed for the "never started playing" fallback above. If
    // playback already started (see videoPlayHandler), the player doesn't
    // need a play() retry, just its `muted` state kept in sync.
    if (!isDeepLinked || !muted || isPlayingRef.current) return;

    setShowUnmuteAffordance(true);
    // react-player only calls the underlying player's play() on a
    // false -> true transition of the `playing` prop, so re-toggling it
    // forces a fresh play attempt now that the player is muted (which the
    // browser allows even without a user gesture).
    setPlaying(false);
    const retryTimer = setTimeout(() => setPlaying(true), 0);

    return () => clearTimeout(retryTimer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [muted]);

  const videoReadyHandler = (player: ReactPlayer) => {
    if (isDeepLinked) {
      player.seekTo(autoPlayStartSeconds ?? 0, "seconds");
    }
  };

  const videoPlayHandler = () => {
    isPlayingRef.current = true;
    clearTimeout(unmuteFallbackTimer.current);

    if (!isDeepLinked) return;

    // YouTube's iframe autoplay policy can silently force-mute playback
    // even when we request unmuted audio, while still firing onPlay
    // normally — so check the underlying player's real mute state instead
    // of trusting our own `muted` prop.
    const internalPlayer = playerRef.current?.getInternalPlayer();
    const isActuallyMuted =
      typeof internalPlayer?.isMuted === "function" && internalPlayer.isMuted();

    if (isActuallyMuted) {
      setMuted(true);
      setShowUnmuteAffordance(true);
    }
  };

  const unmuteClickHandler = () => {
    playerRef.current?.seekTo(autoPlayStartSeconds ?? 0, "seconds");
    setMuted(false);
    setShowUnmuteAffordance(false);
  };

  return (
    <Space direction="vertical" size="medium" justify="start" fullWidth>
      <ExerciseHeader<TDescribeVideoExercisePayload>
        chapterId={chapterId}
        exercise={exercise}
        prefix={prefix}
      />
      <VideoPlayer
        ref={playerRef}
        url={videoUrl}
        controls
        light={isDeepLinked ? false : getYouTubeThumbnail(videoUrl) || true}
        playing={isDeepLinked ? playing : undefined}
        muted={isDeepLinked ? muted : undefined}
        onReady={videoReadyHandler}
        onPlay={videoPlayHandler}
        lazy
        rounded
        volume={0.2}
        width="100%"
        height="100%"
      />
      {showUnmuteAffordance && (
        <Button variant="secondary" onClick={unmuteClickHandler}>
          {LessonDetailsContent.Preview.Action.UNMUTE_VIDEO}
        </Button>
      )}
      <Paragraph variant={2}>{exercise.payload.data.description}</Paragraph>
    </Space>
  );
};
